import { AppointmentOption } from '@components/AppointmentTypePage/AppointmentTypePage.styles';
import {
  LuxAvatar2,
  LuxButton,
  LuxDivider,
  LuxLoader,
  LuxModal,
  LuxModalFooter,
  LuxModalHeader,
  LuxSelectablePill,
  LuxTextField,
  LuxTypography,
  NoResultsIcon,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { MessageError } from '@gemini/lux-ds-pms';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import { DoctorDTO, DoctorInfoResponse } from '@utils/interfaces';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface DoctorsModalProps {
  isOpen: boolean;
  doctors: DoctorInfoResponse;
  onClose: () => void;
  isLoadingDoctors: boolean;
  initialLearnMoreView?: boolean;
  initialDoctor: DoctorDTO | null;
  onBack: () => void;
}

export function OurDoctorsModal({
  isOpen,
  doctors,
  onClose,
  isLoadingDoctors,
  initialDoctor,
  initialLearnMoreView,
  onBack,
}: DoctorsModalProps) {
  const { t } = useCustomTranslation();
  const theme = useTheme();
  const history = useHistory();
  const { Urls } = useUrlsContext();
  const doctorId = useOnlineBookingStore(state => state.doctorId);
  const scheduleConfig = useOnlineBookingStore(state => state.scheduleConfig);
  const setProviderIdFromUrl = useOnlineBookingStore(
    state => state.setProviderIdFromUrl,
  );
  const setDoctorId = useOnlineBookingStore(state => state.setDoctorId);
  const brandName = useOnlineBookingStore(state => state.brandName);
  // const practiceId = useOnlineBookingStore(state => state.practiceId);
  /*  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(
    undefined,
  ); */
  const [learnMoreView, setLearnMoreView] = useState(false);
  const [learnMoreDoctor, setLearnMoreDoctor] = useState<DoctorDTO | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDoctors, setFilteredDoctors] = useState(doctors);
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);

  console.log('docs', filteredDoctors);
  console.log('docId', doctorId);

  useEffect(() => {
    setFilteredDoctors(doctors); // reset quando arrivano nuovi dottori
  }, [doctors]);

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setFilteredDoctors(doctors);
      return;
    }
    const lowerQuery = searchQuery.toLowerCase();
    setFilteredDoctors(
      doctors.filter(doc => doc?.title?.toLowerCase().includes(lowerQuery)),
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getGenderLetter = (gender?: string) => {
    if (!gender) return '';
    const lower = gender.toLowerCase();
    if (lower === 'female') return 'F';
    if (lower === 'male') return 'M';
    return '';
  };

  useEffect(() => {
    if (initialDoctor) {
      setLearnMoreDoctor(initialDoctor);
      setLearnMoreView(true);
    } else if (initialLearnMoreView) {
      setLearnMoreView(true);
    } else {
      setLearnMoreDoctor(null);
      setLearnMoreView(false);
    }
  }, [initialDoctor, initialLearnMoreView]);

  useEffect(() => {
    if (!isOpen) {
      setLearnMoreView(false);
      setLearnMoreDoctor(null);
    }
  }, [isOpen]);

  return (
    <>
      <LuxModal
        size="extra-large"
        open={isOpen}
        onClose={onClose}
        footer={
          (learnMoreView || learnMoreDoctor) && !isMobile ? (
            <LuxModalFooter
              linkButtonText={t('generalCta.back')}
              linkButtonVariant="ghost"
              onLinkButtonClick={() => {
                setLearnMoreView(false);
                setLearnMoreDoctor(null);
                onBack?.();
              }}
              primaryButtonText={
                scheduleConfig?.showDoctorDetailsForWebScheduling
                  ? t('practiceCard.bookWithThis')
                  : ''
              }
              onPrimaryButtonClick={() => {
                setProviderIdFromUrl(doctorId);
                history.push(`${Urls.APPOINTMENT_TYPE}`);
              }}
            />
          ) : (
            (learnMoreView || learnMoreDoctor) &&
            isMobile && (
              <LuxModalFooter
                primaryButtonText={
                  scheduleConfig?.showDoctorDetailsForWebScheduling
                    ? t('practiceCard.bookWithThis')
                    : ''
                }
                primaryButtonSx={{ width: '100%' }}
                onPrimaryButtonClick={() => {
                  setProviderIdFromUrl(doctorId);
                  history.push(`${Urls.APPOINTMENT_TYPE}`);
                }}
              />
            )
          )
        }
        header={
          <LuxModalHeader
            enableCloseIcon
            onClose={onClose}
            title={
              (learnMoreView || learnMoreDoctor) && !isMobile ? (
                <Stack flexDirection="column" gap="0.5rem">
                  <LuxTypography variant="heading2">
                    {learnMoreDoctor?.title}{' '}
                    {learnMoreDoctor?.items.gender === 'male' ||
                    learnMoreDoctor?.items.gender === 'female'
                      ? `(${getGenderLetter(learnMoreDoctor?.items.gender)})`
                      : ''}
                  </LuxTypography>
                  <LuxTypography
                    variant="textMdRegular"
                    color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                  >
                    {learnMoreDoctor?.jobTitle ?? ''}
                  </LuxTypography>
                </Stack>
              ) : learnMoreDoctor && isMobile && learnMoreDoctor ? (
                <LuxButton
                  size="small"
                  variant="ghost"
                  iconStart="ChevronLeftIcon"
                  onClick={() => {
                    setLearnMoreView(false);
                    setLearnMoreDoctor(null);
                  }}
                >
                  {t('practiceCard.seeAll')}
                </LuxButton>
              ) : (
                <LuxTypography variant="heading3">
                  {t('practiceCard.ourDoctors')}
                </LuxTypography>
              )
            }
          ></LuxModalHeader>
        }
      >
        {learnMoreView || learnMoreDoctor ? (
          <Stack
            flexDirection={isMobile ? 'column' : 'row'}
            gap="1.5rem"
            width="100%"
            paddingBottom="1.5rem"
          >
            <div
              style={{
                display: !learnMoreDoctor?.avatar ? 'flex' : 'unset',
                alignItems: !learnMoreDoctor?.avatar ? 'center' : 'unset',
                justifyContent: !learnMoreDoctor?.avatar ? 'center' : 'unset',
                width: '19.75rem',
                height: '17.1875rem',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                flexShrink: 0,
                background: !learnMoreDoctor?.avatar
                  ? theme.palette.LUX.backgrounds.generic.backgroundDarkest
                  : 'unset',
              }}
            >
              {learnMoreDoctor?.avatar ? (
                <img
                  src={learnMoreDoctor.avatar}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <LuxTypography
                  variant="heading2"
                  fontSize={'10rem'}
                  color={theme.palette.LUX.backgrounds.generic.backgroundWhite}
                >
                  {`${learnMoreDoctor?.firstName.toUpperCase().charAt(0)}${learnMoreDoctor?.lastName
                    .toUpperCase()
                    .charAt(0)}`}
                </LuxTypography>
              )}
            </div>
            {isMobile && (
              <Stack flexDirection="column" gap="0.5rem">
                <LuxTypography variant="heading2">
                  {learnMoreDoctor?.title}{' '}
                  {learnMoreDoctor?.items.gender === 'male' ||
                  learnMoreDoctor?.items.gender === 'female'
                    ? `(${getGenderLetter(learnMoreDoctor?.items.gender)})`
                    : ''}
                </LuxTypography>
                <LuxTypography
                  variant="textMdRegular"
                  color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                >
                  {learnMoreDoctor?.jobTitle ?? ''}
                </LuxTypography>
              </Stack>
            )}
            <Stack flexDirection="column">
              <Stack flexDirection="column" gap="1rem" marginBottom="1rem">
                <LuxTypography variant="textMedium">
                  {t('practiceCard.languages')}
                </LuxTypography>
                <Stack flexDirection="row" gap="0.5rem">
                  {learnMoreDoctor?.items.languages?.map((language, index) => (
                    <LuxSelectablePill
                      customColor={
                        theme.palette.LUX.obTokens.bgColorBaseSecondary
                      }
                      customBorderColor={
                        theme.palette.LUX.obTokens.bordersColorNeutralSecondary
                      }
                      key={language ?? index}
                      text={language}
                      selectable={false}
                    />
                  ))}
                </Stack>
              </Stack>
              <LuxDivider />
              <Stack
                flexDirection="column"
                gap="0.25rem"
                marginTop="1rem"
                marginBottom="1rem"
              >
                <LuxTypography variant="textMedium">
                  {t('practiceCard.biography')}
                </LuxTypography>
                <LuxTypography
                  variant="textMdRegular"
                  color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                >
                  {learnMoreDoctor?.items.biography}
                </LuxTypography>
              </Stack>
              <LuxDivider />
              <Stack
                flexDirection="column"
                gap="0.25rem"
                marginTop="1rem"
                marginBottom="1rem"
              >
                <LuxTypography variant="textMedium">
                  {t('practiceCard.specialities')}
                </LuxTypography>
                <LuxTypography
                  variant="textMdRegular"
                  color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                >
                  {learnMoreDoctor?.items.specialty?.join(', ')}
                </LuxTypography>
              </Stack>
              <LuxDivider />
              <Stack flexDirection="column" gap="0.25rem" marginTop="1rem">
                <LuxTypography variant="textMedium">
                  {t('practiceCard.about')}
                </LuxTypography>
                <LuxTypography
                  variant="textMdRegular"
                  color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                >
                  {learnMoreDoctor?.customMessage || '-'}
                </LuxTypography>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack flexDirection="column" gap="1.5rem" height="100%">
            <LuxTextField
              fullWidth={isMobile}
              sx={{ maxWidth: isMobile ? '100%' : '16rem' }}
              icon="SearchIcon"
              adornmentPosition="end"
              label={t('practiceCard.searchByName')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Stack
              flexDirection="column"
              gap="1.5rem"
              height={isMobile ? '100%' : '50svh'}
              paddingBottom="1.5rem"
              overflow="auto"
            >
              {isLoadingDoctors ? (
                <LuxLoader />
              ) : filteredDoctors.length === 0 ? (
                <MessageError
                  icon={<NoResultsIcon />}
                  label={t('practiceCard.noDoctors')}
                />
              ) : (
                <>
                  {filteredDoctors.map(doc => {
                    return (
                      <AppointmentOption
                        style={{
                          maxHeight: 'unset',
                          minHeight: 'unset',
                          height: 'auto',
                        }}
                        key={doc.doctorId}
                        onClick={() => {
                          setDoctorId(doc.doctorId);
                        }}
                        selectedColor={
                          theme.palette.LUX.obTokens?.bordersColorBrandPrimary
                        }
                        color={
                          theme.palette.LUX.obTokens
                            ?.bordersColorNeutralSecondary
                        }
                        minHeight={!brandName}
                      >
                        <Stack
                          flexDirection={isMobile ? 'column' : 'row'}
                          width="100%"
                          gap="1rem"
                          alignItems={isMobile ? 'flex-start' : 'center'}
                        >
                          {!isMobile ? (
                            <LuxAvatar2
                              size="large"
                              src={doc?.avatar}
                              variant={doc?.avatar ? 'image' : 'initials'}
                              firstName={doc.firstName}
                              lastName={doc.lastName}
                            />
                          ) : (
                            <Stack flexDirection="row" gap="0.5rem">
                              <LuxAvatar2
                                size="large"
                                src={doc?.avatar}
                                variant={doc?.avatar ? 'image' : 'initials'}
                                firstName={doc.firstName}
                                lastName={doc.lastName}
                              />
                              <Stack
                                flexDirection="column"
                                gap="0.5rem"
                                alignItems="start"
                              >
                                <LuxTypography
                                  variant="textMedium"
                                  style={{
                                    textWrap: isMobile ? 'nowrap' : 'wrap',
                                  }}
                                >
                                  {doc?.title}
                                </LuxTypography>
                                <LuxTypography
                                  style={{
                                    textWrap: isMobile ? 'nowrap' : 'wrap',
                                  }}
                                  color={
                                    theme.palette.LUX.obTokens
                                      .textColorBaseSecondary
                                  }
                                >
                                  {doc?.jobTitle ?? ''}
                                </LuxTypography>
                              </Stack>
                            </Stack>
                          )}
                          <Stack
                            flexDirection="column"
                            gap="0.5rem"
                            alignItems="flex-start"
                            width={isMobile ? '100%' : '70%'}
                          >
                            {!isMobile && (
                              <Stack flexDirection="row" gap="0.25rem">
                                <LuxTypography
                                  style={{
                                    textWrap: isMobile ? 'nowrap' : 'wrap',
                                  }}
                                  variant="textMedium"
                                >
                                  {doc?.title}
                                </LuxTypography>
                                <LuxTypography
                                  style={{
                                    textWrap: isMobile ? 'nowrap' : 'wrap',
                                  }}
                                  color={
                                    theme.palette.LUX.obTokens
                                      .textColorBaseSecondary
                                  }
                                >
                                  {doc?.jobTitle ?? ''}
                                </LuxTypography>
                              </Stack>
                            )}
                            <LuxTypography
                              color={
                                theme.palette.LUX.obTokens
                                  .textColorBaseSecondary
                              }
                              textAlign="left"
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {doc?.items.biography}
                            </LuxTypography>
                            <LuxButton
                              variant="link"
                              onClick={() => {
                                setLearnMoreView(true);
                                setLearnMoreDoctor(doc);
                              }}
                              iconEnd="ChevronRightIcon"
                            >
                              {t('practiceCard.learnMore')}
                            </LuxButton>
                          </Stack>
                          {scheduleConfig?.showDoctorDetailsForWebScheduling && (
                            <LuxButton
                              sx={{ width: isMobile ? '100%' : 'auto' }}
                              onClick={() => {
                                setProviderIdFromUrl(doc.doctorId);
                                history.push(`${Urls.APPOINTMENT_TYPE}`);
                              }}
                            >
                              {t('practiceCard.bookWithThis')}
                            </LuxButton>
                          )}
                        </Stack>
                      </AppointmentOption>
                    );
                  })}
                </>
              )}
            </Stack>
          </Stack>
        )}
      </LuxModal>
    </>
  );
}
